/**
 * Nowcasting Page - Fund Intelligence Tab
 * 
 * Displays probabilistic classification of post-buy trade evolution:
 * - Stock-level dominant regime and probability
 * - Expected forward gradient signal
 * - Confidence scores
 * - Fund-level breakdown
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Play,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Clock,
  Users,
  Trash2,
  Settings,
  Zap,
  LineChart,
  Square,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Quality styling (drives card COLOR) - matches CurveModelPage
const QUALITY_STYLES = {
  top_tier: { 
    label: "Top Tier", 
    bg: "bg-emerald-500/10", 
    border: "border-emerald-500/30", 
    text: "text-emerald-400",
    dot: "bg-emerald-500",
    chartColor: "#10b981"
  },
  strong: { 
    label: "Strong", 
    bg: "bg-green-500/5", 
    border: "border-green-500/20", 
    text: "text-green-400",
    dot: "bg-green-500",
    chartColor: "#22c55e"
  },
  conditional: { 
    label: "Conditional", 
    bg: "bg-amber-500/5", 
    border: "border-amber-500/20", 
    text: "text-amber-400",
    dot: "bg-amber-500",
    chartColor: "#f59e0b"
  },
  weak: { 
    label: "Weak", 
    bg: "bg-red-500/5", 
    border: "border-red-500/20", 
    text: "text-red-400",
    dot: "bg-red-500",
    chartColor: "#ef4444"
  },
  too_early: {
    label: "Too Early",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
    text: "text-slate-400",
    dot: "bg-slate-500",
    chartColor: "#64748b"
  },
};

// Line chart component for curve visualization with projection support
const CurveChart = ({ 
  curve, 
  projection = null, // Array of projected values (next 3 months)
  color = "#3b82f6", 
  projectionColor = "#9ca3af",
  height = 100, 
  showLabels = true,
  totalBuckets = 12 
}) => {
  if (!curve || curve.length === 0) return null;
  
  const width = 220;
  const padding = { top: 10, right: 10, bottom: showLabels ? 20 : 10, left: showLabels ? 30 : 10 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Combine curve and projection for scaling
  const allValues = projection ? [...curve, ...projection] : curve;
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const range = maxVal - minVal || 1;
  
  // Calculate point positions based on total 12 buckets
  const getX = (idx) => padding.left + (idx / (totalBuckets - 1)) * chartWidth;
  const getY = (val) => padding.top + chartHeight - ((val - minVal) / range) * chartHeight;
  
  // Realized curve points
  const realizedPoints = curve.map((val, idx) => `${getX(idx)},${getY(val)}`).join(" ");
  
  // Projection points (starts from last realized point)
  let projectionPoints = "";
  if (projection && projection.length > 0) {
    const startIdx = curve.length - 1;
    const lastRealizedVal = curve[curve.length - 1];
    projectionPoints = [
      `${getX(startIdx)},${getY(lastRealizedVal)}`,
      ...projection.map((val, idx) => `${getX(startIdx + 1 + idx)},${getY(val)}`)
    ].join(" ");
  }
  
  // Vertical line at observation date
  const obsLineX = getX(curve.length - 1);
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      {/* Grid lines */}
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#374151" strokeWidth="1" />
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#374151" strokeWidth="1" />
      
      {/* Y-axis labels */}
      {showLabels && (
        <>
          <text x={padding.left - 5} y={padding.top + 5} fontSize="9" textAnchor="end" fill="#6b7280">{maxVal.toFixed(2)}</text>
          <text x={padding.left - 5} y={height - padding.bottom} fontSize="9" textAnchor="end" fill="#6b7280">{minVal.toFixed(2)}</text>
        </>
      )}
      
      {/* X-axis labels */}
      {showLabels && (
        <>
          <text x={padding.left} y={height - 3} fontSize="9" textAnchor="middle" fill="#6b7280">M1</text>
          <text x={padding.left + chartWidth / 2} y={height - 3} fontSize="9" textAnchor="middle" fill="#6b7280">M6</text>
          <text x={width - padding.right} y={height - 3} fontSize="9" textAnchor="middle" fill="#6b7280">M12</text>
        </>
      )}
      
      {/* Observation date vertical line */}
      <line 
        x1={obsLineX} 
        y1={padding.top} 
        x2={obsLineX} 
        y2={height - padding.bottom} 
        stroke="#6b7280" 
        strokeWidth="1" 
        strokeDasharray="3,3"
      />
      <text x={obsLineX} y={padding.top - 2} fontSize="8" textAnchor="middle" fill="#9ca3af">now</text>
      
      {/* Realized area fill */}
      <polygon 
        points={`${padding.left},${height - padding.bottom} ${realizedPoints} ${obsLineX},${height - padding.bottom}`}
        fill={color}
        fillOpacity="0.15"
      />
      
      {/* Realized line (solid) */}
      <polyline 
        points={realizedPoints}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Projection line (dotted) */}
      {projection && projection.length > 0 && (
        <>
          {/* Projection area fill */}
          <polygon 
            points={`${obsLineX},${height - padding.bottom} ${projectionPoints} ${getX(curve.length - 1 + projection.length)},${height - padding.bottom}`}
            fill={projectionColor}
            fillOpacity="0.1"
          />
          <polyline 
            points={projectionPoints}
            fill="none"
            stroke={projectionColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="4,4"
          />
          {/* Projection points (hollow circles) */}
          {projection.map((val, idx) => {
            const x = getX(curve.length + idx);
            const y = getY(val);
            return (
              <circle key={`proj-${idx}`} cx={x} cy={y} r="2.5" fill="none" stroke={projectionColor} strokeWidth="1.5" />
            );
          })}
        </>
      )}
      
      {/* Realized points (solid circles) */}
      {curve.map((val, idx) => {
        const x = getX(idx);
        const y = getY(val);
        return (
          <circle key={idx} cx={x} cy={y} r="2.5" fill={color} />
        );
      })}
    </svg>
  );
};

const NowcastingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  // State
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [state, setState] = useState(null);
  const [config, setConfig] = useState(null);
  const [archetypes, setArchetypes] = useState([]);
  const [availableDates, setAvailableDates] = useState(null);
  const [nowcastingData, setNowcastingData] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedObsDate, setSelectedObsDate] = useState("");
  const [testSymbols, setTestSymbols] = useState("AAPL,MSFT,GOOGL");
  const [runStatus, setRunStatus] = useState({ status: "idle", message: "" });
  const [expandedRow, setExpandedRow] = useState(null);
  const [fundBreakdown, setFundBreakdown] = useState([]);
  const [fundBreakdownSource, setFundBreakdownSource] = useState(null); // "hot" | "cold" | "empty"
  const [diagnostics, setDiagnostics] = useState(null);
  const [latestLogs, setLatestLogs] = useState([]);
  const [selectedRunForLogs, setSelectedRunForLogs] = useState(null);
  
  // Batch run state
  const [batchStartDate, setBatchStartDate] = useState("");
  const [batchEndDate, setBatchEndDate] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);
  const [sequentialMode, setSequentialMode] = useState(true); // Default to sequential for reliability
  const [skipExisting, setSkipExisting] = useState(true); // Skip dates that already have data
  
  // Filters
  const [searchSymbol, setSearchSymbol] = useState("");
  const [clusterFilter, setClusterFilter] = useState(null);
  const [ageFilter, setAgeFilter] = useState(null); // null = any, or {min, max} in months
  const [sortBy, setSortBy] = useState("gradient_desc"); // gradient_desc, gradient_asc, age_asc, age_desc
  const [dateFilter, setDateFilter] = useState(null); // null = all dates, or specific date string
  const [dataError, setDataError] = useState(null); // For debugging
  
  // Current batch run tracking
  const [currentRunId, setCurrentRunId] = useState(null);
  const [stopRequested, setStopRequested] = useState(false);
  
  // Ref for auto-scrolling logs
  const logsEndRef = useRef(null);

  // Process nowcasting data with client-side filters (date is handled server-side)
  const processedNowcastingData = useMemo(() => {
    let filtered = [...nowcastingData];
    
    // Date filter is now server-side, no need to filter again
    
    // Apply symbol search (case-insensitive)
    if (searchSymbol) {
      const search = searchSymbol.toUpperCase().trim();
      filtered = filtered.filter(row => row.symbol?.toUpperCase().includes(search));
    }
    
    // Apply cluster filter
    if (clusterFilter !== null) {
      filtered = filtered.filter(row => row.dominant_cluster === clusterFilter);
    }
    
    // Apply age filter
    if (ageFilter) {
      filtered = filtered.filter(row => {
        const age = row.avg_age_buckets || row.avg_age_months || 0;
        switch (ageFilter) {
          case "0-6":
            return age >= 0 && age < 6;
          case "6-9":
            return age >= 6 && age < 9;
          case "9-12":
            return age >= 9 && age < 12;
          case "12+":
            return age >= 12;
          default:
            return true;
        }
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "gradient_desc":
          return (b.expected_gradient_next_1m || 0) - (a.expected_gradient_next_1m || 0);
        case "gradient_asc":
          return (a.expected_gradient_next_1m || 0) - (b.expected_gradient_next_1m || 0);
        case "age_asc":
          return (a.avg_age_buckets || 0) - (b.avg_age_buckets || 0);
        case "age_desc":
          return (b.avg_age_buckets || 0) - (a.avg_age_buckets || 0);
        case "confidence_desc":
          return (b.confidence || 0) - (a.confidence || 0);
        case "funds_desc":
          return (b.n_funds || 0) - (a.n_funds || 0);
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [nowcastingData, searchSymbol, clusterFilter, ageFilter, sortBy]);

  // Auth check
  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  // Load initial data
  useEffect(() => {
    if (token) {
      fetchStats();
      fetchState();
      fetchConfig();
      fetchArchetypes();
      fetchAvailableDates();
      fetchRuns();
      fetchDiagnostics();
    }
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchState = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setState(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch state:", error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setConfig(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  };

  const fetchArchetypes = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/archetypes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setArchetypes(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch archetypes:", error);
    }
  };

  const fetchAvailableDates = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/available-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data);
        if (data.recommended_next?.length > 0 && !selectedObsDate) {
          setSelectedObsDate(data.recommended_next[0]);
        } else if (data.available_dates?.length > 0 && !selectedObsDate) {
          setSelectedObsDate(data.available_dates[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch available dates:", error);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/runs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const runsData = await response.json();
        setRuns(runsData);
        // Auto-select the latest run for logs
        if (runsData.length > 0 && !selectedRunForLogs) {
          setSelectedRunForLogs(runsData[0].run_id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch runs:", error);
    }
  };

  const fetchDiagnostics = async () => {
    try {
      const response = await fetch(`${API}/api/nowcasting/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setDiagnostics(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch diagnostics:", error);
    }
  };

  const fetchLogs = async (runId) => {
    if (!runId) return;
    try {
      const response = await fetch(`${API}/api/nowcasting/runs/${runId}/logs?limit=2000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const logs = await response.json();
        setLatestLogs(logs);
        // Auto-scroll to bottom after logs update
        setTimeout(() => {
          logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  };

  // Fetch logs when selected run changes
  useEffect(() => {
    if (selectedRunForLogs) {
      fetchLogs(selectedRunForLogs);
    }
  }, [selectedRunForLogs]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    setDataError(null);
    try {
      // Build URL with server-side filters for date (3000 to cover all stocks per date)
      let url = `${API}/api/nowcasting/data?limit=3000`;
      
      // Pass date filter to backend to get the right data
      if (dateFilter) {
        url += `&observation_date=${dateFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const responseText = await response.text();
      
      if (response.ok) {
        try {
          const result = JSON.parse(responseText);
          setNowcastingData(result.data || []);
          if (result.error) {
            setDataError(`API returned error: ${result.error}`);
          }
        } catch (parseErr) {
          setDataError(`JSON parse error: ${parseErr.message}. Response: ${responseText.substring(0, 200)}`);
        }
      } else {
        setDataError(`HTTP ${response.status}: ${responseText.substring(0, 200)}`);
      }
    } catch (error) {
      setDataError(`Fetch error: ${error.message}`);
    } finally {
      setDataLoading(false);
    }
  }, [token, dateFilter]);
  
  // Refetch when date filter changes
  useEffect(() => {
    if (activeTab === "data") {
      fetchData();
    }
  }, [activeTab, fetchData, dateFilter]);
  
  // Auto-select latest date when available dates load
  useEffect(() => {
    if (availableDates?.already_computed?.length > 0 && !dateFilter) {
      setDateFilter(availableDates.already_computed[0]);
    }
  }, [availableDates, dateFilter]);

  const fetchFundBreakdown = async (symbol, obsDate, forceCold = false) => {
    try {
      const url = `${API}/api/nowcasting/data/${symbol}/funds?observation_date=${obsDate}${forceCold ? "&force_cold=true" : ""}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setFundBreakdownSource(response.headers.get("X-Data-Source") || null);
        setFundBreakdown(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch fund breakdown:", error);
    }
  };

  const handleRun = async () => {
    if (!selectedObsDate) return;

    setLoading(true);
    setRunStatus({ status: "running", message: "Starting nowcasting..." });

    try {
      const response = await fetch(`${API}/api/nowcasting/run`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          observation_date: selectedObsDate,
          use_modal: true,
        }),
      });

      const result = await response.json();
      
      if (result.run_id) {
        setSelectedRunForLogs(result.run_id);
        // Start polling for logs
        const pollInterval = setInterval(async () => {
          try {
            const logsRes = await fetch(`${API}/api/nowcasting/runs/${result.run_id}/logs?limit=2000`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (logsRes.ok) {
              const logsData = await logsRes.json();
              setLatestLogs(logsData);
            }
          } catch (e) {
            console.error("Log polling error:", e);
          }
        }, 2000);

        // Wait for completion or poll status
        setTimeout(() => clearInterval(pollInterval), 120000); // Max 2 min polling
      }
      
      if (result.status === "success") {
        setRunStatus({ status: "success", message: `Completed: ${result.n_stock_results} stocks processed` });
        fetchStats();
        fetchState();
        fetchRuns();
        if (activeTab === "data") fetchData();
      } else if (result.status === "started" || result.status === "running") {
        setRunStatus({ status: "running", message: result.message || "Processing..." });
        // Auto-select this run for logs and start polling
        setSelectedRunForLogs(result.run_id);
        pollRunStatus(result.run_id);
      } else {
        setRunStatus({ status: "error", message: result.error || "Run failed" });
      }
    } catch (error) {
      // Suppress postMessage cloning errors (browser extension conflicts)
      if (error.message?.includes("postMessage") || error.message?.includes("could not be cloned")) {
        console.debug("Suppressed postMessage error:", error);
        return;
      }
      setRunStatus({ status: "error", message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const pollRunStatus = async (runId) => {
    let attempts = 0;
    const maxAttempts = 180; // 6 minutes (180 * 2s)
    
    const poll = async () => {
      try {
        // Always fetch logs on each poll
        await fetchLogs(runId);
        
        const response = await fetch(`${API}/api/nowcasting/runs/${runId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const status = await response.json();
          
          // Update progress in status message if available
          if (status.progress) {
            setRunStatus({ 
              status: "running", 
              message: `Processing: ${status.progress}` 
            });
          }
          
          if (status.status === "success" || status.status === "completed") {
            setRunStatus({ status: "success", message: `Completed: ${status.n_stock_results || status.n_results || 0} stocks processed` });
            setCurrentRunId(null);
            setBatchLoading(false);  // Stop is now complete
            fetchStats();
            fetchState();
            fetchRuns();
            await fetchLogs(runId); // Final log fetch
            if (activeTab === "data") fetchData();
            return;
          } else if (status.status === "error" || status.status === "failed" || status.status === "stopped") {
            setRunStatus({ status: "error", message: status.error || "Run failed/stopped" });
            setCurrentRunId(null);
            setBatchLoading(false);  // Stop on error too
            await fetchLogs(runId); // Fetch logs to see error
            return;
          }
        }
      } catch (e) {
        // Suppress postMessage cloning errors
        if (e.message?.includes("postMessage") || e.message?.includes("could not be cloned")) {
          console.debug("Suppressed postMessage error in poll:", e);
        } else {
          console.error("Status poll error:", e);
        }
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setRunStatus({ status: "warning", message: "Run may still be processing. Check logs." });
      }
    };
    
    // Start polling immediately
    poll();
  };

  const handleBatchRun = async () => {
    if (!batchStartDate || !batchEndDate) return;

    setBatchLoading(true);
    setRunStatus({ status: "running", message: `Starting batch run from ${batchStartDate} to ${batchEndDate}...` });

    try {
      const response = await fetch(`${API}/api/nowcasting/run/batch`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          start_date: batchStartDate,
          end_date: batchEndDate,
          use_modal: true,
        }),
      });

      const result = await response.json();
      
      if (result.status === "success" || result.status === "completed") {
        setRunStatus({ 
          status: "success", 
          message: `Batch completed: ${result.total_dates || 0} dates, ${result.total_stocks || 0} total stock records` 
        });
        fetchStats();
        fetchState();
        fetchRuns();
        if (activeTab === "data") fetchData();
      } else {
        setRunStatus({ status: "error", message: result.error || "Batch run failed" });
      }
    } catch (error) {
      // Suppress postMessage cloning errors (browser extension conflicts)
      if (error.message?.includes("postMessage") || error.message?.includes("could not be cloned")) {
        console.debug("Suppressed postMessage error:", error);
        return;
      }
      setRunStatus({ status: "error", message: error.message });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleParallelBatch = async (preset) => {
    setBatchLoading(true);
    setStopRequested(false);
    const modeLabel = sequentialMode ? "sequential" : "parallel";
    const skipLabel = skipExisting ? " (skip existing)" : "";
    setRunStatus({ status: "running", message: `Starting ${modeLabel} batch (${preset})${skipLabel}...` });

    try {
      const response = await fetch(`${API}/api/nowcasting/run/parallel-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          preset: preset,
          max_parallel: sequentialMode ? 1 : 10,
          use_modal: true,
          sequential: sequentialMode,
          skip_existing: skipExisting
        }),
      });
      
      const result = await response.json();
      
      if (result.status === "started") {
        const skippedInfo = result.skipped_dates ? ` (${result.skipped_dates} skipped)` : "";
        setRunStatus({ 
          status: "running", 
          message: `${modeLabel.charAt(0).toUpperCase() + modeLabel.slice(1)} batch started: ${result.total_dates} dates${skippedInfo} (${result.date_range?.start} → ${result.date_range?.end})` 
        });
        // Auto-select the run for logs
        if (result.run_id) {
          setSelectedRunForLogs(result.run_id);
          setCurrentRunId(result.run_id);
          pollRunStatus(result.run_id);
        }
        fetchRuns();
      } else if (result.status === "completed" || result.status === "success") {
        setRunStatus({ 
          status: "success", 
          message: `Batch completed: ${result.total_dates || 0} dates, ${result.total_stocks || 0} stocks` 
        });
        setCurrentRunId(null);
        setBatchLoading(false);  // Only set false on completion
        fetchStats();
        fetchState();
        fetchRuns();
        if (activeTab === "data") fetchData();
      } else {
        setRunStatus({ status: "error", message: result.error || result.detail || "Batch failed" });
        setBatchLoading(false);  // Set false on error
      }
    } catch (error) {
      if (error.message?.includes("postMessage") || error.message?.includes("could not be cloned")) {
        console.debug("Suppressed postMessage error:", error);
        return;
      }
      setRunStatus({ status: "error", message: error.message });
      setBatchLoading(false);  // Set false on exception
    }
    // Note: Don't set batchLoading=false in finally - the batch runs in background
  };

  const handleParallelBatchCustom = async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    setBatchLoading(true);
    setStopRequested(false);
    const modeLabel = sequentialMode ? "sequential" : "parallel";
    const skipLabel = skipExisting ? " (skip existing)" : "";
    setRunStatus({ status: "running", message: `Starting ${modeLabel} batch (${startDate} → ${endDate})${skipLabel}...` });

    try {
      const response = await fetch(`${API}/api/nowcasting/run/parallel-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          max_parallel: sequentialMode ? 1 : 10,
          use_modal: true,
          sequential: sequentialMode,
          skip_existing: skipExisting
        }),
      });
      
      const result = await response.json();
      
      if (result.status === "started" || result.status === "completed" || result.status === "success") {
        const skippedInfo = result.skipped_dates ? ` (${result.skipped_dates} skipped)` : "";
        setRunStatus({ 
          status: result.status === "started" ? "running" : "success", 
          message: result.message || `Processing ${result.total_dates || 0} dates${skippedInfo}` 
        });
        if (result.run_id) {
          setSelectedRunForLogs(result.run_id);
          pollRunStatus(result.run_id);
        }
        // Only set batchLoading false if completed immediately (not started)
        if (result.status !== "started") {
          setBatchLoading(false);
        }
        fetchStats();
        fetchState();
        fetchRuns();
        if (activeTab === "data") fetchData();
      } else {
        setRunStatus({ status: "error", message: result.error || result.detail || "Batch failed" });
        setBatchLoading(false);
      }
    } catch (error) {
      if (error.message?.includes("postMessage") || error.message?.includes("could not be cloned")) {
        console.debug("Suppressed postMessage error:", error);
        return;
      }
      setRunStatus({ status: "error", message: error.message });
      setBatchLoading(false);
    }
    // Note: Don't set batchLoading=false in finally - the batch runs in background
  };

  const handleStopBatch = async () => {
    setStopRequested(true);
    setRunStatus({ status: "warning", message: "Stop requested... finishing current batch" });
    
    // The stop is handled by setting stopRequested which the polling checks
    // For a hard stop, we'd need a backend endpoint to cancel the Modal job
    try {
      if (currentRunId) {
        await fetch(`${API}/api/nowcasting/runs/${currentRunId}/stop`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      console.log("Stop request sent");
    }
    
    setBatchLoading(false);
    setStopRequested(false);
  };

  const handleClearData = async () => {
    if (!confirm("Are you sure you want to clear all nowcasting data?")) return;

    try {
      const response = await fetch(`${API}/api/nowcasting/data`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchStats();
        fetchState();
        fetchData();
      }
    } catch (error) {
      console.error("Failed to clear data:", error);
    }
  };

  const handleTestRun = async () => {
    if (!selectedObsDate || !testSymbols) return;

    setTestLoading(true);
    setRunStatus({ status: "running", message: "Running test..." });

    try {
      const response = await fetch(
        `${API}/api/nowcasting/run/test?observation_date=${selectedObsDate}&test_symbols=${encodeURIComponent(testSymbols)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await response.json();
      
      if (result.status === "success") {
        setRunStatus({ 
          status: "success", 
          message: `Test completed: ${result.n_stock_results || 0} stocks, ${result.n_fund_results || 0} fund records` 
        });
        fetchStats();
        fetchState();
        fetchRuns();
        if (activeTab === "data") fetchData();
      } else {
        setRunStatus({ status: "error", message: result.error || result.message || "Test failed" });
      }
    } catch (error) {
      setRunStatus({ status: "error", message: error.message });
    } finally {
      setTestLoading(false);
    }
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `${Math.round(value * 100)}%`;
  };

  const formatGradient = (value) => {
    if (value === null || value === undefined) return "N/A";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${(value * 100).toFixed(2)}%`;
  };

  // Get quality style for a cluster
  const getClusterStyle = (clusterId) => {
    const qualityMap = {
      0: "strong",       // Front-Run Exhaustion
      1: "strong",       // Compounding Drift
      2: "conditional",  // Shakeout Recovery
      3: "weak",         // Overcrowded Peak
      4: "strong",       // Delayed Inflection
      5: "top_tier",     // Momentum Lock-In
      6: "strong",       // Late Ignition
      7: "weak",         // Distribution Phase
      8: "strong",       // Slow Build Momentum
    };
    const quality = qualityMap[clusterId] || "conditional";
    return QUALITY_STYLES[quality] || QUALITY_STYLES.conditional;
  };

  // ============== RENDER ==============

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="h-6 w-6 text-purple-400" />
              Nowcasting Engine
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Probabilistic regime classification for active buy positions
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchStats(); fetchState(); }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Total Entries</div>
              <div className="text-2xl font-bold">{stats?.total_stocks || 0}</div>
            </CardContent>
          </Card>
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Latest Obs Date</div>
              <div className="text-lg font-semibold text-amber-400">
                {availableDates?.max_date || state?.date_range?.newest || "N/A"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Latest Run</div>
              <div className="text-lg font-semibold">
                {state?.latest_run?.observation_date || "None"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Data Range</div>
              <div className="text-sm font-semibold">
                {availableDates?.min_date?.slice(0, 7) || "?"} → {availableDates?.max_date?.slice(0, 7) || "?"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">Status</div>
              <Badge variant={state?.latest_run?.status === "completed" ? "default" : "secondary"}>
                {state?.latest_run?.status || "Ready"}
              </Badge>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-xl">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data">Results</TabsTrigger>
            <TabsTrigger value="archetypes" data-testid="tab-archetypes">Archetypes</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Run Panel */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="h-5 w-5" />
                    Run Nowcasting
                  </CardTitle>
                  <CardDescription>
                    Compute regime probabilities for active buy positions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Data Range Info */}
                  {availableDates?.data_range && (
                    <div className="text-xs bg-blue-500/10 border border-blue-500/20 rounded p-2">
                      <span className="font-medium">Data Range:</span>{" "}
                      {availableDates.data_range.min_date} to {availableDates.data_range.max_date}
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-xs">Observation Date</Label>
                    <Select value={selectedObsDate} onValueChange={setSelectedObsDate}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select date" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDates?.available_dates?.map((date) => (
                          <SelectItem key={date} value={date}>
                            {date}
                            {availableDates?.already_computed?.includes(date) && " (computed)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Test Run Section */}
                  <div className="border rounded-lg p-3 bg-muted/30">
                    <Label className="text-xs font-semibold">Quick Test</Label>
                    <div className="mt-2">
                      <Input
                        placeholder="AAPL,MSFT,GOOGL"
                        value={testSymbols}
                        onChange={(e) => setTestSymbols(e.target.value)}
                        className="h-8 text-sm font-mono"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Comma-separated symbols</p>
                    </div>
                    <Button
                      onClick={handleTestRun}
                      disabled={testLoading || !selectedObsDate || !testSymbols}
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                      data-testid="test-nowcasting-btn"
                    >
                      {testLoading ? (
                        <>
                          <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Play className="h-3 w-3 mr-2" />
                          Run Test
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Full Run Button */}
                  <Button
                    onClick={handleRun}
                    disabled={loading || !selectedObsDate}
                    className="w-full"
                    data-testid="run-nowcasting-btn"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Running Full...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Run Full Nowcasting
                      </>
                    )}
                  </Button>

                  {runStatus && runStatus.status !== "idle" && (
                    <div className={`p-3 rounded text-sm ${
                      runStatus.status === "success" ? "bg-green-500/10 text-green-400" :
                      runStatus.status === "error" ? "bg-red-500/10 text-red-400" :
                      runStatus.status === "warning" ? "bg-yellow-500/10 text-yellow-400" :
                      "bg-blue-500/10 text-blue-400"
                    }`}>
                      <div className="flex justify-between items-center">
                        <span>{runStatus.message}</span>
                        {(runStatus.status === "running" || batchLoading) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={handleStopBatch}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Batch Run Section - Enhanced with Presets */}
                  <div className="border rounded-lg p-3 bg-muted/30 mt-4">
                    <Label className="text-xs font-semibold">Batch / Historical Run</Label>
                    
                    {/* Sequential Mode Toggle */}
                    <div className="flex items-center gap-2 mt-2 mb-2 p-2 bg-background/50 rounded">
                      <input
                        type="checkbox"
                        id="sequentialMode"
                        checked={sequentialMode}
                        onChange={(e) => setSequentialMode(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-600"
                        disabled={batchLoading || runStatus?.status === "running"}
                      />
                      <label htmlFor="sequentialMode" className="text-xs text-muted-foreground">
                        Sequential Mode {sequentialMode ? "(slower, more reliable)" : "(parallel, faster)"}
                      </label>
                    </div>
                    
                    {/* Skip Existing Toggle */}
                    <div className="flex items-center gap-2 mb-2 p-2 bg-background/50 rounded">
                      <input
                        type="checkbox"
                        id="skipExisting"
                        checked={skipExisting}
                        onChange={(e) => setSkipExisting(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-600"
                        disabled={batchLoading || runStatus?.status === "running"}
                      />
                      <label htmlFor="skipExisting" className="text-xs text-muted-foreground">
                        Skip dates with existing data {skipExisting ? "(resume mode)" : "(recompute all)"}
                      </label>
                    </div>
                    
                    {/* Direct DB Mode Toggle - Phase 2 Optimization */}
                    <div className="flex items-center gap-2 mb-3 p-2 bg-background/50 rounded border border-blue-500/20">
                      <input
                        type="checkbox"
                        id="directDbMode"
                        checked={config?.modal_direct_db_load || false}
                        onChange={async (e) => {
                          try {
                            const response = await fetch(`${API}/api/nowcasting/config`, {
                              method: "PUT",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({ modal_direct_db_load: e.target.checked }),
                            });
                            if (response.ok) {
                              setConfig({ ...config, modal_direct_db_load: e.target.checked });
                            }
                          } catch (err) {
                            console.error("Failed to update config:", err);
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-600"
                        disabled={batchLoading || runStatus?.status === "running"}
                        data-testid="direct-db-toggle"
                      />
                      <label htmlFor="directDbMode" className="text-xs text-muted-foreground">
                        Direct DB Mode {config?.modal_direct_db_load ? 
                          <span className="text-blue-400">(Modal loads from MongoDB - faster)</span> : 
                          "(standard transfer)"}
                      </label>
                    </div>
                    
                    {/* Preset Buttons */}
                    <div className="flex gap-2 mb-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleParallelBatch("last_1y")}
                        disabled={batchLoading || runStatus?.status === "running"}
                      >
                        Last 1Y
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleParallelBatch("last_2y")}
                        disabled={batchLoading || runStatus?.status === "running"}
                      >
                        Last 2Y
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={() => handleParallelBatch("last_5y")}
                        disabled={batchLoading || runStatus?.status === "running"}
                      >
                        Last 5Y
                      </Button>
                    </div>
                    
                    {/* Action Buttons Row - Full Historical + Stop */}
                    <div className="flex gap-2 mb-2">
                      <Button
                        onClick={() => handleParallelBatch("all")}
                        variant="default"
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        disabled={batchLoading || runStatus?.status === "running"}
                        data-testid="full-historical-btn"
                      >
                        <Zap className="h-3 w-3 mr-2" />
                        Full Historical
                      </Button>
                      <Button
                        onClick={handleStopBatch}
                        variant="destructive"
                        className="px-4"
                        disabled={false}
                        data-testid="stop-batch-btn"
                      >
                        <Square className="h-4 w-4 fill-current" />
                        <span className="ml-2">Stop</span>
                      </Button>
                    </div>
                    
                    {/* Retry Failed Dates Button */}
                    <Button
                      onClick={async () => {
                        try {
                          setRunStatus({ status: "running", message: "Checking for failed dates..." });
                          const failedRes = await fetch(`${API}/api/nowcasting/failed-dates?limit=50`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          const failedData = await failedRes.json();
                          
                          if (failedData.count === 0) {
                            setRunStatus({ status: "idle", message: "No failed dates to retry" });
                            return;
                          }
                          
                          setBatchLoading(true);
                          setRunStatus({ status: "running", message: `Retrying ${failedData.count} failed dates...` });
                          
                          const response = await fetch(`${API}/api/nowcasting/retry-failed?max_dates=20&sequential=true`, {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          const result = await response.json();
                          
                          if (result.run_id) {
                            setSelectedRunForLogs(result.run_id);
                            pollRunStatus(result.run_id);
                          }
                          
                          setRunStatus({ 
                            status: result.succeeded > 0 ? "success" : "warning", 
                            message: `Retried: ${result.succeeded}/${result.total_retried} succeeded` 
                          });
                          setBatchLoading(false);
                          fetchRuns();
                        } catch (error) {
                          setRunStatus({ status: "error", message: error.message });
                          setBatchLoading(false);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 text-xs text-amber-600 border-amber-600/30 hover:bg-amber-600/10"
                      disabled={batchLoading || runStatus?.status === "running"}
                      data-testid="retry-failed-btn"
                    >
                      <RefreshCw className="h-3 w-3 mr-2" />
                      Retry Failed Dates
                    </Button>
                    
                    <div className="text-xs text-muted-foreground mt-2 text-center">
                      Uses Modal parallel processing (10x faster)
                    </div>
                    
                    {/* Custom Date Range - with simple date inputs */}
                    <details className="mt-3">
                      <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                        Custom date range...
                      </summary>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Start Date</Label>
                          <Input
                            type="date"
                            value={batchStartDate}
                            onChange={(e) => setBatchStartDate(e.target.value)}
                            className="h-8 text-xs"
                            placeholder="YYYY-MM-DD"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">End Date</Label>
                          <Input
                            type="date"
                            value={batchEndDate}
                            onChange={(e) => setBatchEndDate(e.target.value)}
                            className="h-8 text-xs"
                            placeholder="YYYY-MM-DD"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={() => handleParallelBatchCustom(batchStartDate, batchEndDate)}
                        disabled={batchLoading || !batchStartDate || !batchEndDate}
                        variant="secondary"
                        className="w-full mt-2"
                        size="sm"
                        data-testid="batch-nowcasting-btn"
                      >
                        Run Custom Range (Parallel)
                      </Button>
                    </details>
                  </div>
                </CardContent>
              </Card>

              {/* Cluster Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Cluster Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.cluster_distribution?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.cluster_distribution.map((item) => (
                        <div key={item.cluster} className="flex items-center gap-2">
                          <div className="w-32 text-xs truncate">{item.cluster}</div>
                          <Progress
                            value={(item.count / stats.total_stocks) * 100}
                            className="flex-1"
                          />
                          <div className="w-12 text-xs text-right">{item.count}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground text-sm text-center py-8">
                      No data yet. Run nowcasting to see results.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="data" className="space-y-4">
            {/* Header with observation date */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold">Nowcasting Results</h3>
                {state?.latest_run?.observation_date && (
                  <Badge variant="outline" className="text-sm">
                    Observation Date: {state.latest_run.observation_date}
                  </Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                {nowcastingData.length} stocks
              </div>
            </div>
            
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[150px]">
                    <Label className="text-xs">Symbol</Label>
                    <Input
                      placeholder="Search symbol..."
                      value={searchSymbol}
                      onChange={(e) => setSearchSymbol(e.target.value)}
                      className="h-9"
                      data-testid="symbol-filter-input"
                    />
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Observation Date</Label>
                    <Select
                      value={dateFilter || "all"}
                      onValueChange={(v) => setDateFilter(v === "all" ? null : v)}
                    >
                      <SelectTrigger className="h-9" data-testid="date-filter-select">
                        <SelectValue placeholder="All Dates" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dates</SelectItem>
                        {availableDates?.already_computed?.slice(0, 30).map((date, idx) => (
                          <SelectItem key={date} value={date}>
                            {date}{idx === 0 ? ' (Latest)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label className="text-xs">Archetype / Cluster</Label>
                    <Select
                      value={clusterFilter?.toString() || "any"}
                      onValueChange={(v) => setClusterFilter(v === "any" ? null : parseInt(v))}
                    >
                      <SelectTrigger className="h-9" data-testid="cluster-filter-select">
                        <SelectValue placeholder="All Archetypes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">All Archetypes</SelectItem>
                        <SelectItem value="0">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Front-Run Exhaustion
                          </span>
                        </SelectItem>
                        <SelectItem value="1">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Compounding Drift
                          </span>
                        </SelectItem>
                        <SelectItem value="2">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            Shakeout Recovery
                          </span>
                        </SelectItem>
                        <SelectItem value="3">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            Overcrowded Peak
                          </span>
                        </SelectItem>
                        <SelectItem value="4">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Delayed Inflection
                          </span>
                        </SelectItem>
                        <SelectItem value="5">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            Momentum Lock-In
                          </span>
                        </SelectItem>
                        <SelectItem value="6">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Late Ignition
                          </span>
                        </SelectItem>
                        <SelectItem value="7">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            Distribution Phase
                          </span>
                        </SelectItem>
                        <SelectItem value="8">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Slow Build Momentum
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">Trade Age</Label>
                    <Select
                      value={ageFilter || "any"}
                      onValueChange={(v) => setAgeFilter(v === "any" ? null : v)}
                    >
                      <SelectTrigger className="h-9" data-testid="age-filter-select">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Age</SelectItem>
                        <SelectItem value="0-6">0-6 months (Fresh)</SelectItem>
                        <SelectItem value="6-9">6-9 months</SelectItem>
                        <SelectItem value="9-12">9-12 months</SelectItem>
                        <SelectItem value="12+">12+ months (Mature)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Sort By</Label>
                    <Select
                      value={sortBy}
                      onValueChange={(v) => setSortBy(v)}
                    >
                      <SelectTrigger className="h-9" data-testid="sort-select">
                        <SelectValue placeholder="Sort..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gradient_desc">Gradient ↓ (Best)</SelectItem>
                        <SelectItem value="gradient_asc">Gradient ↑ (Worst)</SelectItem>
                        <SelectItem value="age_asc">Age ↑ (Youngest)</SelectItem>
                        <SelectItem value="age_desc">Age ↓ (Oldest)</SelectItem>
                        <SelectItem value="confidence_desc">Confidence ↓</SelectItem>
                        <SelectItem value="funds_desc">Funds ↓</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={fetchData} size="sm" variant="outline" data-testid="refresh-btn" disabled={dataLoading}>
                      <RefreshCw className={`h-4 w-4 mr-2 ${dataLoading ? 'animate-spin' : ''}`} />
                      {dataLoading ? 'Loading...' : 'Refresh'}
                    </Button>
                  </div>
                </div>
                {/* Active Filters Display */}
                {(clusterFilter !== null || searchSymbol || ageFilter || dateFilter) && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                    <span className="text-xs text-muted-foreground">Active filters:</span>
                    {dateFilter && (
                      <Badge variant="secondary" className="text-xs">
                        Date: {dateFilter}
                        <button 
                          className="ml-1 hover:text-red-400" 
                          onClick={() => setDateFilter(null)}
                        >×</button>
                      </Badge>
                    )}
                    {searchSymbol && (
                      <Badge variant="secondary" className="text-xs">
                        Symbol: {searchSymbol}
                        <button 
                          className="ml-1 hover:text-red-400" 
                          onClick={() => setSearchSymbol("")}
                        >×</button>
                      </Badge>
                    )}
                    {clusterFilter !== null && (
                      <Badge variant="secondary" className="text-xs">
                        Cluster: {archetypes.find(a => a.cluster_id === clusterFilter)?.name || `#${clusterFilter}`}
                        <button 
                          className="ml-1 hover:text-red-400" 
                          onClick={() => setClusterFilter(null)}
                        >×</button>
                      </Badge>
                    )}
                    {ageFilter && (
                      <Badge variant="secondary" className="text-xs">
                        Age: {ageFilter}mo
                        <button 
                          className="ml-1 hover:text-red-400" 
                          onClick={() => setAgeFilter(null)}
                        >×</button>
                      </Badge>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => {
                        setSearchSymbol("");
                        setClusterFilter(null);
                        setAgeFilter(null);
                        setDateFilter(null);
                      }}
                    >
                      Clear all
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Debug Error Display */}
            {dataError && (
              <Card className="border-red-500 bg-red-500/10">
                <CardContent className="p-4">
                  <div className="text-red-400 text-sm font-mono">
                    <strong>Debug Error:</strong> {dataError}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Table */}
            <Card>
              <CardHeader className="py-2 px-4 border-b">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Showing {processedNowcastingData.length} of {nowcastingData.length} results
                    {ageFilter && ` (filtered by age: ${ageFilter}mo)`}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Sorted by: {sortBy.replace("_", " ").replace("desc", "↓").replace("asc", "↑")}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card border-b">
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Symbol</TableHead>
                        <TableHead>Obs Date</TableHead>
                        <TableHead>Regime</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">µAlpha 3M</TableHead>
                        <TableHead className="text-right" title="P(regime thesis plays out). For weak regimes (Overcrowded Peak, Distribution Phase) this is P(underperforms) — shown with ↓">Conviction</TableHead>
                        <TableHead className="text-right">Probability</TableHead>
                        <TableHead className="text-right">Confidence</TableHead>
                        <TableHead className="text-right">Gradient 1m</TableHead>
                        <TableHead className="text-right">Gradient 3m</TableHead>
                        <TableHead className="text-right">Funds</TableHead>
                        <TableHead className="text-right">Avg Age</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dataLoading ? (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-8">
                            <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : processedNowcastingData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-12 text-muted-foreground">
                            <div className="space-y-3">
                              <p className="font-medium">No nowcasting results available</p>
                              <p className="text-sm">Results appear here after a successful nowcasting run.</p>
                              <div className="text-xs space-y-1">
                                <p>Make sure:</p>
                                <p>1. Buy events exist in the database (check Logs tab → Data Prerequisites)</p>
                                <p>2. The observation date is within your data's date range</p>
                                <p>3. A nowcasting run completed successfully with {">"} 0 results</p>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setActiveTab("logs")}
                                className="mt-2"
                              >
                                View Diagnostics
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        processedNowcastingData.map((row) => (
                          <React.Fragment key={row.symbol}>
                              <TableRow className="hover:bg-muted/50">
                                <TableCell>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (expandedRow === row.symbol) {
                                        setExpandedRow(null);
                                      } else {
                                        setExpandedRow(row.symbol);
                                        fetchFundBreakdown(row.symbol, row.observation_date);
                                      }
                                    }}
                                  >
                                    {expandedRow === row.symbol ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </button>
                                </TableCell>
                                <TableCell className="font-mono font-medium">
                                  {row.symbol}
                                  {row.crowded && (
                                    <span className="ml-1 px-1 py-0.5 rounded border border-red-500/40 text-red-400 text-[9px] font-mono align-middle"
                                          title="Top-decile crowding, not in a strong regime (−2.34%/3M vs SPY, t −3.49)"
                                          data-testid={`nowcast-crowded-${row.symbol}`}>CROWDED</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">{row.observation_date}</TableCell>
                                <TableCell>
                                  {(() => {
                                    const style = getClusterStyle(row.dominant_cluster);
                                    return (
                                      <Badge
                                        className={`text-xs ${style.bg} ${style.border} ${style.text} border`}
                                      >
                                        {row.dominant_cluster_name}
                                      </Badge>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell>
                                  {row.options_state ? (
                                    <Badge
                                      data-testid={`microstate-chip-${row.symbol}`}
                                      className={`text-[10px] border ${
                                        row.options_state === "quiet"
                                          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                          : row.options_state === "hot"
                                          ? "bg-red-500/15 text-red-400 border-red-500/30"
                                          : "bg-muted text-muted-foreground border-border"
                                      }`}
                                    >
                                      {row.options_state.charAt(0).toUpperCase() + row.options_state.slice(1)}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground" title="No options coverage (outside top-600 mkt cap)">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-mono text-xs">
                                  {row.microstate_alpha_3m != null ? (
                                    <span className={row.microstate_alpha_3m >= 0 ? "text-green-400" : "text-red-400"}>
                                      {row.microstate_alpha_3m > 0 ? "+" : ""}{row.microstate_alpha_3m.toFixed(1)}%
                                    </span>
                                  ) : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-xs">
                                  {row.conviction_score != null ? (() => {
                                    const isWeakRegime = row.dominant_cluster === 3 || row.dominant_cluster === 7;
                                    const v = (row.conviction_score * 100).toFixed(0);
                                    return isWeakRegime ? (
                                      <span
                                        data-testid={`conviction-${row.symbol}`}
                                        title="Weak regime — score is P(underperforms): conviction the bearish regime plays out"
                                        className={row.conviction_score >= 0.55 ? "text-red-400 font-semibold cursor-help" : "text-muted-foreground cursor-help"}
                                      >
                                        ↓{v}
                                      </span>
                                    ) : (
                                      <span
                                        data-testid={`conviction-${row.symbol}`}
                                        title="Score is P(delivers): conviction the regime thesis plays out"
                                        className={`cursor-help ${row.conviction_score >= 0.55 ? "text-green-400 font-semibold" : row.conviction_score < 0.45 ? "text-red-400" : "text-muted-foreground"}`}
                                      >
                                        {v}
                                      </span>
                                    );
                                  })() : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {formatPercent(row.dominant_probability)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className={row.confidence >= 0.7 ? "text-green-400" : "text-muted-foreground"}>
                                    {formatPercent(row.confidence)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  <span className={row.expected_gradient_next_1m >= 0 ? "text-green-400 font-semibold" : "text-red-400 font-semibold"}>
                                    {formatGradient(row.expected_gradient_next_1m)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  <span className={row.expected_gradient_next_3m >= 0 ? "text-green-400" : "text-red-400"}>
                                    {formatGradient(row.expected_gradient_next_3m)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right">{row.n_funds}</TableCell>
                                <TableCell className="text-right text-muted-foreground">
                                  {row.avg_age_buckets?.toFixed(1) || row.avg_age_months?.toFixed(1) || '-'}mo
                                </TableCell>
                              </TableRow>
                              
                              {/* Expanded Row */}
                              {expandedRow === row.symbol && (
                                <TableRow className="bg-muted/30">
                                  <TableCell colSpan={13} className="p-4">
                                    {/* Data-source diagnostic strip (hybrid hot/cold visibility) */}
                                    {expandedRow === row.symbol && fundBreakdownSource && (
                                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/40">
                                        <div className="flex items-center gap-2 text-xs">
                                          <span className="text-muted-foreground">Fund-level source:</span>
                                          <span
                                            data-testid="fund-source-badge"
                                            className={
                                              fundBreakdownSource === "cold"
                                                ? "px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-mono font-semibold"
                                                : fundBreakdownSource === "hot"
                                                ? "px-2 py-0.5 rounded bg-orange-500/15 text-orange-300 font-mono font-semibold"
                                                : "px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono"
                                            }
                                          >
                                            {fundBreakdownSource === "cold" ? "🧊 ARCHIVE (R2)" :
                                             fundBreakdownSource === "hot"  ? "⚡ LIVE (Mongo)" :
                                                                              "∅ EMPTY"}
                                          </span>
                                          <span className="text-muted-foreground">
                                            ({fundBreakdown.length} fund{fundBreakdown.length !== 1 ? "s" : ""})
                                          </span>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 px-2 text-xs"
                                          data-testid="force-cold-fetch-btn"
                                          onClick={() => fetchFundBreakdown(row.symbol, row.observation_date, true)}
                                        >
                                          Verify cold path
                                        </Button>
                                      </div>
                                    )}
                                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                                      {/* Probability Bars */}
                                      <div>
                                        <h4 className="text-sm font-semibold mb-2">Cluster Probabilities</h4>
                                        <div className="space-y-1">
                                          {archetypes.map((arch) => (
                                            <div key={arch.cluster_id} className="flex items-center gap-2">
                                              <div className="w-24 text-xs truncate">{arch.name}</div>
                                              <Progress
                                                value={(row[`p_cluster_${arch.cluster_id}`] || 0) * 100}
                                                className="flex-1 h-2"
                                              />
                                              <div className="w-12 text-xs text-right">
                                                {formatPercent(row[`p_cluster_${arch.cluster_id}`])}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Gradient Details */}
                                      <div>
                                        <h4 className="text-sm font-semibold mb-2">Expected Gradient</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div className="bg-background/50 rounded p-3">
                                            <div className="text-xs text-muted-foreground mb-1">Next 1 Month</div>
                                            <div className={`text-xl font-bold ${row.expected_gradient_next_1m >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                              {row.expected_gradient_next_1m >= 0 ? '+' : ''}{(row.expected_gradient_next_1m * 100).toFixed(2)}%
                                            </div>
                                          </div>
                                          <div className="bg-background/50 rounded p-3">
                                            <div className="text-xs text-muted-foreground mb-1">Next 3 Months</div>
                                            <div className={`text-xl font-bold ${row.expected_gradient_next_3m >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                              {row.expected_gradient_next_3m >= 0 ? '+' : ''}{(row.expected_gradient_next_3m * 100).toFixed(2)}%
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-2">
                                          Trade Age: {row.avg_age_buckets?.toFixed(1)} buckets | Obs: {row.observation_date}
                                        </div>
                                      </div>
                                      
                                      {/* Alpha Curve Visualization */}
                                      <div>
                                        <h4 className="text-sm font-semibold mb-2">Alpha Curve + Projection</h4>
                                        {fundBreakdown.length > 0 && expandedRow === row.symbol ? (
                                          <div className="bg-background/50 rounded p-2">
                                            {(() => {
                                              // Get the best fund (should be only one per symbol now)
                                              const eligibleFund = fundBreakdown.find(f => 
                                                (f.partial_curve_values_raw?.length > 0 || f.partial_curve_values?.length > 0)
                                              );
                                              
                                              if (!eligibleFund) {
                                                return (
                                                  <div className="text-xs text-muted-foreground text-center py-4">
                                                    No curve data available
                                                  </div>
                                                );
                                              }
                                              
                                              // Use raw curve if available, otherwise fall back to normalized
                                              const rawCurve = eligibleFund.partial_curve_values_raw || eligibleFund.partial_curve_values;
                                              const nBuckets = rawCurve.length;
                                              const lastRawVal = rawCurve[nBuckets - 1];
                                              const dominantArch = archetypes.find(a => a.cluster_id === row.dominant_cluster);
                                              
                                              // Build extended curve: raw realized + projected future values
                                              let extendedCurve = [...rawCurve];
                                              let projectionStartIdx = nBuckets;
                                              
                                              if (nBuckets < 12) {
                                                const grad1m = row.expected_gradient_next_1m || 0;
                                                const grad3m = row.expected_gradient_next_3m || 0;
                                                
                                                // Project next 3 months using the gradient
                                                // Month 1: apply 1m gradient
                                                const proj1 = lastRawVal * (1 + grad1m);
                                                extendedCurve.push(proj1);
                                                
                                                // Month 2 & 3: use 3m average gradient
                                                if (nBuckets + 1 < 12) {
                                                  const monthlyGrad3m = grad3m / 3; // Average per month
                                                  const proj2 = proj1 * (1 + monthlyGrad3m);
                                                  extendedCurve.push(proj2);
                                                  
                                                  if (nBuckets + 2 < 12) {
                                                    const proj3 = proj2 * (1 + monthlyGrad3m);
                                                    extendedCurve.push(proj3);
                                                  }
                                                }
                                              }
                                              
                                              // Normalize the ENTIRE extended curve to [0, 1]
                                              const minVal = Math.min(...extendedCurve);
                                              const maxVal = Math.max(...extendedCurve);
                                              const range = maxVal - minVal || 1;
                                              const normalizedCurve = extendedCurve.map(v => (v - minVal) / range);
                                              
                                              // Split back into realized and projection
                                              const realizedCurve = normalizedCurve.slice(0, nBuckets);
                                              const projection = normalizedCurve.slice(nBuckets);
                                              
                                              // Color based on gradient direction
                                              const gradientIsPositive = (row.expected_gradient_next_1m || 0) >= 0;
                                              const projectionColor = gradientIsPositive ? "#22c55e" : "#ef4444"; // green or red
                                              
                                              return (
                                                <>
                                                  <CurveChart 
                                                    curve={realizedCurve}
                                                    projection={projection.length > 0 ? projection : null}
                                                    color={QUALITY_STYLES[dominantArch?.quality || 'conditional']?.chartColor || '#3b82f6'}
                                                    projectionColor={projectionColor}
                                                    height={100}
                                                    showLabels={true}
                                                    totalBuckets={12}
                                                  />
                                                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                                    <span>Top eligible fund: {nBuckets} buckets</span>
                                                    <span className={row.expected_gradient_next_1m >= 0 ? "text-green-400" : "text-red-400"}>
                                                      Forecast: {row.expected_gradient_next_1m >= 0 ? '↑' : '↓'} {Math.abs(row.expected_gradient_next_1m * 100).toFixed(1)}%
                                                    </span>
                                                  </div>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-muted-foreground bg-background/50 rounded p-4 text-center">
                                            Click to load curve data
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Archetypes Tab */}
          <TabsContent value="archetypes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChart className="h-5 w-5" />
                  9 Canonical Archetypes
                </CardTitle>
                <CardDescription>
                  Post-buy cumulative alpha curve patterns learned from historical data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {archetypes.map((arch) => {
                    const quality = arch.quality || "conditional";
                    const style = QUALITY_STYLES[quality] || QUALITY_STYLES.conditional;
                    
                    return (
                      <Card 
                        key={arch.cluster_id} 
                        className={`border ${style.border} ${style.bg}`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                              <span className="text-xs text-muted-foreground">#{arch.cluster_id}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {arch.samples || ""} samples
                            </span>
                          </div>
                          <CardTitle className="text-base font-semibold mt-1">{arch.name}</CardTitle>
                          <p className="text-xs text-muted-foreground">{arch.behavior}</p>
                          <div className="flex justify-between items-center mt-1">
                            <span className={`text-xs font-medium ${style.text}`}>
                              {style.label}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {arch.timing}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {/* Line chart curve visualization */}
                          <CurveChart 
                            curve={arch.curve} 
                            color={style.chartColor} 
                            height={100}
                            showLabels={true}
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-2 px-1">
                            <span>Start: {arch.start_value?.toFixed(3) || "N/A"}</span>
                            <span>End: {arch.end_value?.toFixed(3) || "N/A"}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            {/* Diagnostics Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Data Prerequisites
                </CardTitle>
                <CardDescription>
                  Check if all required data is available for nowcasting
                </CardDescription>
              </CardHeader>
              <CardContent>
                {diagnostics ? (
                  <div className="space-y-4">
                    {/* Status Badge */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Status:</span>
                      <Badge variant={diagnostics.status === "ok" ? "default" : "destructive"}>
                        {diagnostics.status === "ok" ? "Ready" : "Missing Data"}
                      </Badge>
                    </div>
                    
                    {/* Issues */}
                    {diagnostics.issues?.length > 0 && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-red-400 mb-2">Issues Found:</h4>
                        <ul className="list-disc list-inside text-sm text-red-300 space-y-1">
                          {diagnostics.issues.map((issue, idx) => (
                            <li key={idx}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Collections Status */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold">Collections:</h4>
                      <div className="grid gap-2">
                        {Object.entries(diagnostics.collections || {}).map(([name, info]) => (
                          <div key={name} className="flex items-center justify-between bg-muted/30 rounded px-3 py-2">
                            <div>
                              <span className="font-mono text-sm">{name}</span>
                              <p className="text-xs text-muted-foreground">{info.required_for}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{info.count?.toLocaleString() || (info.exists ? "✓" : "✗")}</span>
                              <div className={`w-2 h-2 rounded-full ${
                                info.status === "ok" ? "bg-green-500" : "bg-red-500"
                              }`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Date Info */}
                    {diagnostics.date_info?.buy_events && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold">Buy Events Date Range:</h4>
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-muted-foreground">Min Date:</span>{" "}
                              <span className="font-mono">{diagnostics.date_info.buy_events.min_date}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Max Date:</span>{" "}
                              <span className="font-mono">{diagnostics.date_info.buy_events.max_date}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">New Events:</span>{" "}
                              <span className="font-mono">{diagnostics.date_info.buy_events.new_events_count?.toLocaleString()}</span>
                            </div>
                          </div>
                          {diagnostics.date_info.sample_format && (
                            <div className="mt-2 pt-2 border-t border-blue-500/20">
                              <span className="text-muted-foreground">Sample:</span>{" "}
                              <span className="font-mono text-xs">
                                {diagnostics.date_info.sample_format.cusip_symbol} | 
                                CIK: {diagnostics.date_info.sample_format.cik} | 
                                Date: {diagnostics.date_info.sample_format.observation_date}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Button variant="outline" size="sm" onClick={fetchDiagnostics}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Diagnostics
                    </Button>
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm">Loading diagnostics...</div>
                )}
              </CardContent>
            </Card>

            {/* Run Logs Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Run Logs
                </CardTitle>
                <CardDescription>
                  Detailed logs from nowcasting runs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Run Selector */}
                <div className="flex items-center gap-4">
                  <Label className="text-xs">Select Run:</Label>
                  <Select value={selectedRunForLogs || ""} onValueChange={setSelectedRunForLogs}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Select a run" />
                    </SelectTrigger>
                    <SelectContent>
                      {runs.map((run) => (
                        <SelectItem key={run.run_id} value={run.run_id}>
                          {run.observation_date} - {run.run_type} ({run.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => fetchLogs(selectedRunForLogs)}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
                
                {/* Logs Display */}
                <ScrollArea className="h-[500px] border rounded-lg bg-zinc-950 p-4">
                  {latestLogs.length === 0 ? (
                    <div className="text-muted-foreground text-sm text-center py-8">
                      {selectedRunForLogs ? "No logs available for this run" : "Select a run to view logs"}
                    </div>
                  ) : (
                    <div className="space-y-1 font-mono text-xs">
                      {latestLogs.map((log, idx) => (
                        <div 
                          key={idx} 
                          className={`py-0.5 ${
                            log.level === "error" ? "text-red-400" :
                            log.level === "warning" ? "text-yellow-400" :
                            log.message?.includes("⚠️") ? "text-yellow-400" :
                            log.message?.includes("💡") ? "text-blue-400" :
                            "text-gray-300"
                          }`}
                        >
                          <span className="text-gray-500">
                            {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ""}
                          </span>
                          {" "}
                          <span className={`uppercase ${
                            log.level === "error" ? "text-red-500" :
                            log.level === "warning" ? "text-yellow-500" :
                            "text-green-500"
                          }`}>
                            [{log.level}]
                          </span>
                          {" "}
                          {log.message}
                        </div>
                      ))}
                      <div ref={logsEndRef} />
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Run History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Observation Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Results</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No runs yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      runs.map((run) => (
                        <TableRow key={run.run_id}>
                          <TableCell className="font-mono">{run.observation_date}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{run.run_type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={run.status === "completed" ? "default" : run.status === "failed" ? "destructive" : "secondary"}>
                              {run.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {run.started_at ? new Date(run.started_at).toLocaleString() : "-"}
                          </TableCell>
                          <TableCell>
                            {run.metadata?.n_stock_results || "-"} stocks
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default NowcastingPage;
